import React from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

/** The ring sweeps in, then the badge arrives — these have to stay in step. */
export const RING_DELAY_MS = 300
export const RING_DURATION_MS = 900

interface HighlightRingProps {
    /** Distance from the host's edges, e.g. `4` to keep the ring clear of its neighbours. */
    inset?: number
}

/**
 * A pill outline that sweeps itself around its host once, then stays as the host's border. The
 * host needs `position: relative`.
 *
 * It is a plain bordered box revealed by `clip-path`, not an SVG stroke: a dashed stroke has to
 * know the perimeter to cover it, and a dash shorter than the path leaves the far side of a wide
 * element undrawn. A clip reveal cannot miss a side, whatever the label's width.
 */
export const HighlightRing: React.FC<HighlightRingProps> = ({ inset = 0 }) => (
    <Box
        aria-hidden
        sx={{
            position: 'absolute',
            inset,
            borderRadius: '999px',
            border: '2px solid',
            borderColor: 'secondary.main',
            pointerEvents: 'none',
            // The clip is held 4px outside the box on every side it is not sweeping from, so the
            // finished ring has no clip edge running along its border: an edge that coincides with
            // the border's own antialiases it away unevenly, which reads as a thinner top and
            // bottom than sides.
            clipPath: 'inset(-4px 100% -4px -4px round 999px)',
            animation: `highlightRingSweep ${RING_DURATION_MS}ms ease-out ${RING_DELAY_MS}ms forwards`,
            '@keyframes highlightRingSweep': {
                to: { clipPath: 'inset(-4px -4px -4px -4px round 999px)' }
            },
            // No sweep to watch: the ring is simply there.
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                clipPath: 'none'
            }
        }}
    />
)

/**
 * Holds a badge back until the ring has closed, so the two read as one gesture rather than two
 * things happening at once. `both` keeps it invisible until its turn instead of flashing on paint.
 */
export const badgeRevealSx: SxProps<Theme> = {
    display: 'inline-flex',
    animation: `highlightBadgeIn 260ms ease-out ${RING_DELAY_MS + RING_DURATION_MS}ms both`,
    '@keyframes highlightBadgeIn': {
        from: { opacity: 0, transform: 'scale(0.8)' },
        to: { opacity: 1, transform: 'scale(1)' }
    },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
}

export default HighlightRing
